import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Palmtree, Clock, Thermometer, Users, Megaphone, BarChart3, ArrowRight, UserMinus, Handshake, LayoutGrid } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { PageHeader, StatCard, Panel, Empty } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { dateLocale } from "@/lib/i18n";
import { api, fmtDate, fmtDateTime } from "@/lib/api";

const TYPE_LABEL = { ferie: "Ferie", permesso: "Permesso", malattia: "Malattia" };

function AnnouncementsPanel({ items }) {
  return (
    <Panel title="Ultime comunicazioni" action={<Link to="/comunicazioni" className="text-xs text-primary hover:underline">Vedi tutte</Link>} testId="dashboard-announcements">
      {items.length === 0 ? (
        <p className="px-5 py-8 text-sm text-muted-foreground">Nessuna comunicazione.</p>
      ) : (
        <ul className="divide-y">
          {items.map((a) => (
            <li key={a.id}>
              <Link to={`/comunicazioni/${a.id}`} className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-muted/60" data-testid={`dashboard-announcement-${a.id}`}>
                {!a.read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{a.title}</span>
                  <span className="text-xs text-muted-foreground">{fmtDateTime(a.created_at)}</span>
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function AdminDash({ d }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Ferie da approvare" value={d.pending_leaves} icon={Palmtree} to="/ferie" testId="stat-pending-leaves" accent={d.pending_leaves > 0} />
        <StatCard label="Permessi da approvare" value={d.pending_permissions} icon={Clock} to="/permessi" testId="stat-pending-permissions" accent={d.pending_permissions > 0} />
        <StatCard label="Malattie da visionare" value={d.sick_to_ack} icon={Thermometer} to="/malattie" testId="stat-sick-to-ack" accent={d.sick_to_ack > 0} />
        <StatCard label="Dipendenti attivi" value={d.employees} icon={Users} to="/dipendenti" testId="stat-employees" />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <Panel title="Assenti oggi" className="lg:col-span-2" testId="dashboard-absent-today">
          {d.absent_today.length === 0 ? (
            <div className="p-5"><Empty icon={UserMinus} text="Nessuna assenza oggi." /></div>
          ) : (
            <ul className="divide-y">
              {d.absent_today.map((a) => (
                <li key={`${a.user_id}-${a.type}-${a.start}-${a.time || ""}`} className="flex items-center justify-between px-5 py-3 text-sm">
                  <span className="font-medium">{a.user_name}</span>
                  <span className="text-xs text-muted-foreground">{TYPE_LABEL[a.type]} {a.time || `fino al ${fmtDate(a.end)}`}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <div className="lg:col-span-3"><AnnouncementsPanel items={d.announcements} /></div>
      </div>
      <h2 className="mb-4 mt-10 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Le mie assenze e i miei incontri</h2>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="admin-own-section">
        <BalanceCard label={`Le mie ferie ${d.balance.year}`} total={d.balance.leave_total} used={d.balance.leave_used} pending={d.balance.leave_pending} remaining={d.balance.leave_remaining} unit="giorni" testId="admin-balance-leaves" />
        <BalanceCard label={`I miei permessi ${d.balance.year}`} total={d.balance.permission_total} used={d.balance.permission_used} pending={d.balance.permission_pending} remaining={d.balance.permission_remaining} unit="ore" testId="admin-balance-permissions" />
        <MeetingsPanel items={d.upcoming_meetings} />
      </div>
    </>
  );
}

function BalanceCard({ label, total, used, pending, remaining, unit, testId }) {
  if (remaining === undefined) {
    return (
      <div className="lift rounded-lg border bg-card p-6" data-testid={testId}>
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-display mt-3 text-4xl font-bold" data-testid={`${testId}-used`}>{used}<span className="ml-1.5 text-base font-medium text-muted-foreground">{unit} consumati</span></p>
        <p className="mt-2.5 text-xs text-muted-foreground">In attesa di approvazione {pending} {unit}</p>
      </div>
    );
  }
  const pct = total ? Math.min(100, ((used + pending) / total) * 100) : 0;
  return (
    <div className="lift rounded-lg border bg-card p-6" data-testid={testId}>
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-display mt-3 text-4xl font-bold">{remaining}<span className="ml-1.5 text-base font-medium text-muted-foreground">{unit} residui</span></p>
      <Progress value={pct} className="mt-4 h-1.5" />
      <p className="mt-2.5 text-xs text-muted-foreground">Usati {used} · In attesa {pending} · Totale annuo {total}</p>
    </div>
  );
}

function PollsPanel({ polls }) {
  return (
    <Panel title="Sondaggi da votare" testId="dashboard-polls">
      {polls.length === 0 ? (
        <div className="p-5"><Empty icon={BarChart3} text="Nessun sondaggio in attesa del tuo voto." /></div>
      ) : (
        <ul className="divide-y">
          {polls.map((p) => (
            <li key={p.id}>
              <Link to={`/sondaggi/${p.id}`} className="flex items-center justify-between px-5 py-3.5 text-sm hover:bg-muted/60">
                <span className="font-medium">{p.question}</span><ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function MeetingsPanel({ items }) {
  return (
    <Panel title="Prossimi incontri" action={<Link to="/incontri" className="text-xs text-primary hover:underline">Vedi tutti</Link>} testId="dashboard-meetings">
      {items.length === 0 ? (
        <div className="p-5"><Empty icon={Handshake} text="Nessun incontro in programma." /></div>
      ) : (
        <ul className="divide-y">
          {items.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
              <span className="font-medium">{m.topic}</span>
              <span className="whitespace-nowrap text-xs text-muted-foreground">{fmtDateTime(m.scheduled_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function EmployeeDash({ d }) {
  const w = new Set(d.widgets);
  const b = d.balance;
  const stats = w.has("pending_requests") || w.has("unread_announcements");
  const lists = ["announcements", "polls", "meetings"].filter((k) => w.has(k));
  if (w.size === 0) return <Empty icon={LayoutGrid} text="Usa il menu per accedere a ferie, permessi, malattie e agli altri servizi." testId="dashboard-empty" />;
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {w.has("leave_balance") && <BalanceCard label={`Ferie ${b.year}`} total={b.leave_total} used={b.leave_used} pending={b.leave_pending} remaining={b.leave_remaining} unit="giorni" testId="balance-leaves" />}
        {w.has("permission_balance") && <BalanceCard label={`Permessi ${b.year}`} total={b.permission_total} used={b.permission_used} pending={b.permission_pending} remaining={b.permission_remaining} unit="ore" testId="balance-permissions" />}
        {stats && (
          <div className="grid grid-cols-2 gap-4">
            {w.has("pending_requests") && <StatCard label="Richieste in attesa" value={d.pending_leaves + d.pending_permissions} icon={Clock} to="/ferie" testId="stat-my-pending" />}
            {w.has("unread_announcements") && <StatCard label="Da leggere" value={d.unread_announcements} icon={Megaphone} to="/comunicazioni" testId="stat-unread-announcements" accent={d.unread_announcements > 0} />}
          </div>
        )}
      </div>
      {lists.length > 0 && (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {w.has("announcements") && <AnnouncementsPanel items={d.announcements} />}
          {w.has("polls") && <PollsPanel polls={d.polls_to_vote} />}
          {w.has("meetings") && <MeetingsPanel items={d.upcoming_meetings} />}
        </div>
      )}
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  const hour = new Date().getHours();
  const hello = hour < 13 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";
  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`${hello}, ${user.name.split(" ")[0]}`} subtitle={new Date().toLocaleDateString(dateLocale(), { weekday: "long", day: "2-digit", month: "long", year: "numeric" })} />
      {d && (d.role === "admin" ? <AdminDash d={d} /> : <EmployeeDash d={d} />)}
    </div>
  );
}
